import { CircleCheck } from 'lucide-react';
import { PLATFORM_STATS, SPORT_OPTIONS } from 'modules/jogador/home/content';
import { motion, Variants } from 'framer-motion';
import { Button } from 'shared/components/ui/Button';
import { AnimatedNumber } from 'shared/components/motion/AnimatedNumber';
import { EASE_OUT } from 'shared/components/motion/tokens';
import { HeroProductPreview } from 'modules/jogador/home/components/HeroProductPreview';

// Entrada em cascata do hero: selo → título → texto → busca → números.
const heroContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const heroItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE_OUT } },
};

interface HeroSearchSectionProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  sportId: string;
  onSportChange: (value: string) => void;
}

export function HeroSearchSection({ searchTerm, onSearchTermChange, sportId, onSportChange }: HeroSearchSectionProps) {
  return (
    <section className="px-4 py-6">
      <motion.div
        variants={heroContainer}
        initial="hidden"
        animate="visible"
        className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl border border-border/60 bg-surface p-8 shadow-card sm:p-12"
      >
        {/* "Sol" e "mar" desfocados flutuando atrás do conteúdo. */}
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary/25 blur-3xl"
          animate={{ scale: [1, 1.15, 1], x: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-accent/70 blur-3xl"
          animate={{ scale: [1, 1.2, 1], y: [0, -20, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Duas colunas no desktop: proposta + busca à esquerda, o produto funcionando à direita. */}
        <div className="relative grid items-center gap-10 lg:grid-cols-[1.1fr_1fr] xl:gap-16">
          <div>
            <motion.span
              variants={heroItem}
              className="relative inline-flex items-center gap-2 rounded-full bg-accent-soft/80 px-4 py-1.5 text-sm font-medium text-primary backdrop-blur"
            >
              <CircleCheck className="h-4 w-4" aria-hidden="true" />
              Fim do WhatsApp caótico e comprovantes soltos!
            </motion.span>

            <motion.h1
              variants={heroItem}
              className="relative mt-5 max-w-2xl font-heading text-3xl font-extrabold text-ink sm:text-5xl sm:leading-tight"
            >
              Reserve sua quadra em segundos e{' '}
              <span className="animate-shimmer bg-[linear-gradient(110deg,#ff943d,#b86b2c,#ff943d,#ffb070,#ff943d)] bg-[length:200%_100%] bg-clip-text text-transparent">
                divida o Pix
              </span>{' '}
              sem dor de cabeça
            </motion.h1>

            <motion.p variants={heroItem} className="relative mt-5 max-w-xl text-lg text-ink-secondary">
              Encontre arenas de Beach Tennis, Futevôlei, Pickleball e Vôlei de Praia em Fortaleza. Pague sozinho ou
              rache o valor com sua dupla direto na plataforma.
            </motion.p>

            <motion.form
              variants={heroItem}
              onSubmit={(event) => event.preventDefault()}
              className="relative mt-6 flex flex-col gap-2 rounded-2xl border border-border/60 bg-background/90 p-2 backdrop-blur transition-all duration-300 focus-within:border-primary/60 focus-within:shadow-glow sm:flex-row"
            >
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => onSearchTermChange(event.target.value)}
                placeholder="Arena ou bairro (ex: Aldeota)"
                className="flex-1 rounded-xl bg-transparent px-3 py-2 text-sm text-ink outline-none"
              />

              <select
                value={sportId}
                onChange={(event) => onSportChange(event.target.value)}
                className="rounded-xl border border-border bg-surface px-3 py-2 text-sm text-ink outline-none sm:w-52"
              >
                {SPORT_OPTIONS.map((sport) => (
                  <option key={sport.id} value={sport.id}>
                    {sport.id === 'todos' ? 'Todos os Esportes' : sport.label}
                  </option>
                ))}
              </select>

              <Button type="submit">Pesquisar</Button>
            </motion.form>
          </div>

          <motion.div variants={heroItem} className="hidden lg:block">
            <HeroProductPreview />
          </motion.div>
        </div>

        <motion.div
          variants={heroItem}
          className="relative mt-10 grid grid-cols-2 gap-4 border-t border-border/60 pt-6 sm:grid-cols-4"
        >
          {PLATFORM_STATS.map((stat) => (
            <div key={stat.label}>
              <AnimatedNumber
                value={stat.value}
                className="block font-heading text-xl font-extrabold text-primary sm:text-3xl"
              />
              <p className="text-xs text-ink-secondary sm:text-sm">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
